import { Box, Text } from 'grommet';
import { ColorType } from 'grommet/utils';
import { Project, TProjectId } from 'lib/api';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useProjectStore, useUserStore } from 'src/context';
import { pathForProjectSwitch } from 'src/utils/projectPath';
import { Selector, TalonTip } from '../ui';
import { FavoriteFilled } from '../ui/icons/FilledIcons';

export type ProjectSelectorProps = {
  textColor?: ColorType;
};

export const ProjectSelector = observer(({ textColor = 'accent-1' }: ProjectSelectorProps) => {
  /** Context **/
  const navigate = useNavigate();
  const location = useLocation();
  const projectStore = useProjectStore();
  const userStore = useUserStore();

  /** State **/
  const [projectList, setProjectList] = useState(projectStore.projectList);
  const [favorites, setFavorites] = useState<string[]>([]);

  /** Methods **/

  // Only the user's choice here navigates, and only to keep a project page's URL naming the
  // project shown (ProjectRoute selects the project a URL names). This used to run whenever the
  // current project changed for any reason, sending the user to the project's home page each time
  const selectProject = (projectId: TProjectId) => {
    if (projectId === projectStore.currentProject?.id) return;
    const project = projectStore.setCurrentProjectId(projectId);
    const path = project && pathForProjectSwitch(location.pathname, project.name);
    if (path) navigate(path);
  };

  /** Effects **/

  useEffect(() => {
    const getProjects = async () => {
      await projectStore.fetchProjectList();
    };
    getProjects();
  }, [projectStore]);

  useEffect(() => {
    const favs = projectStore.projectList?.filter((p) => userStore.user?.favorite_project_ids?.includes(p.id));
    favs?.sort((a, b) => a.name.localeCompare(b.name));
    const notFavs = projectStore.projectList?.filter((p) => !userStore.user?.favorite_project_ids?.includes(p.id));
    notFavs?.sort((a, b) => a.name.localeCompare(b.name));
    setProjectList([...(favs || []), ...(notFavs || [])]);
  }, [projectStore.projectList, userStore.user?.favorite_project_ids]);

  useEffect(() => {
    setFavorites(userStore.user?.favorite_project_ids || []);
  }, [userStore.user?.favorite_project_ids]);

  /** Render **/
  return (
    <>
      {projectStore.currentProject?.id && (
        <TalonTip content={<Text>Select a project</Text>}>
          <Box alignSelf="center">
            <Selector
              options={projectList}
              value={projectStore.currentProject.id}
              onChange={(val) => selectProject(val)}
              labelKey={(p: Project) =>
                favorites.includes(p.id) ? (
                  <Box direction="row" pad="small" align="center">
                    <FavoriteFilled filled="true" size="small" />
                    <Text size="1rem" weight={500} color={textColor}>
                      &nbsp; {p.name}
                    </Text>
                  </Box>
                ) : (
                  <Box pad="small">
                    <Text size="1rem" color={textColor}>
                      {p.name}
                    </Text>
                  </Box>
                )
              }
            />
          </Box>
        </TalonTip>
      )}
    </>
  );
});
